import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import { ArrowLeft, ArrowRight, CheckCircle2, Lock, Send } from "lucide-react";
import Reveal, { EASE } from "@/components/Reveal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const MOTIVI = [
  "Emorroidi",
  "Ragadi anali",
  "Fistola perianale",
  "Cisti pilonidale",
  "Ascesso",
  "Diverticolosi",
  "Prolasso rettale",
  "Seconda opinione online",
  "Non lo so, preferisco parlarne",
];
const TIPI = ["Visita in studio", "Videoconsulenza"];
const TEMPI = ["Prima possibile", "Entro 2 settimane", "Entro un mese", "Voglio solo informazioni"];

const STEPS = ["Il motivo", "La visita", "I tuoi contatti"];

const emptyForm = { nome: "", cognome: "", telefono: "", email: "", messaggio: "", consenso: false };

export default function Booking() {
  const [step, setStep] = useState(0);
  const [motivo, setMotivo] = useState("");
  const [tipo, setTipo] = useState("");
  const [tempi, setTempi] = useState("");
  const [form, setForm] = useState(emptyForm);
  const [sending, setSending] = useState(false);
  const [done, setDone] = useState(null);

  useEffect(() => {
    const onPreset = (e) => {
      setMotivo(e.detail.motivo);
      setTipo(e.detail.tipo);
      setStep(1);
    };
    window.addEventListener("rdl:preset", onPreset);
    return () => window.removeEventListener("rdl:preset", onPreset);
  }, []);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value });

  const submit = async () => {
    if (form.nome.trim().length < 2 || form.cognome.trim().length < 2) {
      toast.error("Inserisci nome e cognome");
      return;
    }
    if (form.telefono.trim().length < 6) {
      toast.error("Inserisci un numero di telefono valido");
      return;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) {
      toast.error("Inserisci una email valida");
      return;
    }
    if (!form.consenso) {
      toast.error("Serve il tuo consenso per essere ricontattato");
      return;
    }
    setSending(true);
    try {
      const res = await axios.post(`${API}/leads`, {
        nome: form.nome.trim(),
        cognome: form.cognome.trim(),
        telefono: form.telefono.trim(),
        email: form.email.trim(),
        motivo,
        tipo_visita: tipo,
        tempi,
        messaggio: form.messaggio.trim(),
      });
      setDone(res.data);
      toast.success("Richiesta inviata! Controlla la tua email.");
    } catch (e) {
      toast.error("Invio non riuscito, riprova tra poco.");
    } finally {
      setSending(false);
    }
  };

  const restart = () => {
    setStep(0);
    setMotivo("");
    setTipo("");
    setTempi("");
    setForm(emptyForm);
    setDone(null);
  };

  return (
    <section id="prenota" className="relative py-28 overflow-hidden">
      <div className="absolute -bottom-40 -left-32 w-[520px] h-[520px] rounded-full bg-gold/8 blur-[140px]" />
      <div className="max-w-3xl mx-auto px-6">
        <Reveal className="text-center">
          <p className="eyebrow">Prenota</p>
          <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl mt-4 tracking-tight">
            Tre passaggi e sei a casa. <span className="italic text-gold">Nessun tabù.</span>
          </h2>
          <p className="mt-5 text-silver font-light text-lg">
            Ti ricontatto personalmente, con calma. Le tue informazioni restano rigorosamente
            riservate.
          </p>
          <div className="mt-7 flex flex-col sm:flex-row justify-center gap-3" data-testid="booking-path-buttons">
            <button
              onClick={() => setTipo("Visita in studio")}
              data-testid="booking-path-palermo"
              className="inline-flex items-center justify-center gap-2 bg-gold text-ink font-semibold rounded-full px-7 py-3.5 hover:bg-goldlight transition-colors"
            >
              Prenota a Palermo
            </button>
            <button
              onClick={() => { setMotivo("Seconda opinione online"); setTipo("Videoconsulenza"); setStep(1); }}
              data-testid="booking-path-online"
              className="inline-flex items-center justify-center gap-2 border border-sagelight/50 text-sagelight font-semibold rounded-full px-7 py-3.5 hover:bg-sagelight/15 transition-colors"
            >
              Informazioni seconda opinione online
            </button>
          </div>
        </Reveal>

        <Reveal delay={0.12}>
          <div className="mt-12 rounded-[2rem] border border-white/12 bg-card/70 backdrop-blur p-6 sm:p-10 relative overflow-hidden">
            <div className="absolute top-0 inset-x-0 h-1 bg-gradient-to-r from-gold/0 via-gold to-gold/0" />

            <AnimatePresence mode="wait">
              {done ? (
                <motion.div
                  key="success"
                  initial={{ opacity: 0, y: 24 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, ease: EASE }}
                  className="text-center py-6"
                  data-testid="booking-success-panel"
                >
                  <span className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-gold/15 border border-gold/30 text-gold">
                    <CheckCircle2 size={30} />
                  </span>
                  <h3 className="font-display text-3xl mt-6">
                    Grazie {form.nome}, richiesta inviata!
                  </h3>
                  <p className="text-silver leading-relaxed mt-4 max-w-md mx-auto">
                    Ti ho appena inviato una email di conferma
                    {done.email_status?.paziente ? " (controlla anche lo spam)" : " (arriverà appena il servizio email sarà attivo)"}
                    . Ti ricontatterò personalmente al numero{" "}
                    <span className="text-ivory">{form.telefono}</span> il prima possibile.
                  </p>
                  <button
                    onClick={restart}
                    data-testid="booking-restart-button"
                    className="mt-8 inline-flex items-center gap-2 border border-white/15 rounded-full px-6 py-3 text-sm text-ivory hover:border-gold/50 hover:text-gold transition-all"
                  >
                    Torna all'inizio
                  </button>
                </motion.div>
              ) : (
                <motion.div key={`step-${step}`} initial={{ opacity: 0, x: 30 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -30 }} transition={{ duration: 0.4, ease: EASE }}>
                  {/* progress */}
                  <div className="flex items-center justify-between gap-2 mb-9">
                    {STEPS.map((label, i) => (
                      <div key={label} className="flex items-center gap-2 flex-1 last:flex-none">
                        <span
                          className={`font-mono-accent text-xs w-8 h-8 rounded-full flex items-center justify-center border transition-all ${
                            i <= step ? "bg-gold text-ink border-gold" : "text-silver border-white/15"
                          }`}
                        >
                          {i + 1}
                        </span>
                        <span className={`hidden sm:block text-xs uppercase tracking-[0.15em] ${i <= step ? "text-ivory" : "text-silver/60"}`}>
                          {label}
                        </span>
                        {i < STEPS.length - 1 && <span className={`flex-1 h-px ${i < step ? "bg-gold/50" : "bg-white/10"}`} />}
                      </div>
                    ))}
                  </div>

                  {step === 0 && (
                    <div>
                      <h3 className="font-display text-2xl mb-6">Di cosa vuoi occuparti?</h3>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        {MOTIVI.map((m) => (
                          <button
                            key={m}
                            onClick={() => setMotivo(m)}
                            data-testid={`funnel-step-1-option-${m.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                            className={`rounded-2xl border px-5 py-4 text-left text-sm transition-all duration-300 ${
                              motivo === m
                                ? "border-gold bg-gold/10 text-ivory"
                                : "border-white/12 text-silver hover:border-gold/40 hover:text-ivory"
                            }`}
                          >
                            {m}
                          </button>
                        ))}
                      </div>
                      <button
                        onClick={() => {
                          if (!motivo) return;
                          if (motivo === "Seconda opinione online" && !tipo) setTipo("Videoconsulenza");
                          setStep(1);
                        }}
                        disabled={!motivo}
                        data-testid="funnel-step-1-next"
                        className="mt-8 w-full flex items-center justify-center gap-2 bg-gold text-ink font-semibold rounded-full py-3.5 disabled:opacity-30 disabled:cursor-not-allowed hover:bg-goldlight transition-colors"
                      >
                        Continua <ArrowRight size={17} />
                      </button>
                    </div>
                  )}

                  {step === 1 && (
                    <div>
                      <h3 className="font-display text-2xl mb-3">Come preferisci la visita?</h3>
                      <p className="text-xs uppercase tracking-[0.2em] text-silver mb-3">Motivo: <span className="text-gold normal-case tracking-normal">{motivo}</span></p>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        {TIPI.map((t) => (
                          <button
                            key={t}
                            onClick={() => setTipo(t)}
                            data-testid={`funnel-step-2-tipo-${t.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                            className={`rounded-2xl border px-5 py-4 text-left text-sm transition-all duration-300 ${
                              tipo === t ? "border-gold bg-gold/10 text-ivory" : "border-white/12 text-silver hover:border-gold/40 hover:text-ivory"
                            }`}
                          >
                            {t}
                          </button>
                        ))}
                      </div>
                      {tipo === "Videoconsulenza" && (
                        <p className="text-xs text-silver mt-3" data-testid="funnel-step-2-online-note">
                          Perfetta per la seconda opinione: ti colleghi da dove sei, con i tuoi esami già in mano.
                        </p>
                      )}
                      <h3 className="font-display text-2xl mb-3 mt-8">Quando?</h3>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        {TEMPI.map((t) => (
                          <button
                            key={t}
                            onClick={() => setTempi(t)}
                            data-testid={`funnel-step-2-tempi-${t.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                            className={`rounded-2xl border px-5 py-4 text-left text-sm transition-all duration-300 ${
                              tempi === t ? "border-gold bg-gold/10 text-ivory" : "border-white/12 text-silver hover:border-gold/40 hover:text-ivory"
                            }`}
                          >
                            {t}
                          </button>
                        ))}
                      </div>
                      <div className="flex gap-3 mt-8">
                        <button
                          onClick={() => setStep(0)}
                          data-testid="funnel-step-2-back"
                          className="flex items-center gap-2 border border-white/15 rounded-full px-6 py-3.5 text-sm text-silver hover:text-ivory hover:border-white/30 transition-all"
                        >
                          <ArrowLeft size={16} /> Indietro
                        </button>
                        <button
                          onClick={() => tipo && tempi && setStep(2)}
                          disabled={!tipo || !tempi}
                          data-testid="funnel-step-2-next"
                          className="flex-1 flex items-center justify-center gap-2 bg-gold text-ink font-semibold rounded-full py-3.5 disabled:opacity-30 disabled:cursor-not-allowed hover:bg-goldlight transition-colors"
                        >
                          Continua <ArrowRight size={17} />
                        </button>
                      </div>
                    </div>
                  )}

                  {step === 2 && (
                    <div>
                      <h3 className="font-display text-2xl mb-6">I tuoi contatti</h3>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <input className="field-dark px-5 py-3.5 text-sm" placeholder="Nome *" data-testid="booking-input-nome" value={form.nome} onChange={set("nome")} />
                        <input className="field-dark px-5 py-3.5 text-sm" placeholder="Cognome *" data-testid="booking-input-cognome" value={form.cognome} onChange={set("cognome")} />
                        <input className="field-dark px-5 py-3.5 text-sm" placeholder="Telefono *" type="tel" data-testid="booking-input-telefono" value={form.telefono} onChange={set("telefono")} />
                        <input className="field-dark px-5 py-3.5 text-sm" placeholder="Email *" type="email" data-testid="booking-input-email" value={form.email} onChange={set("email")} />
                      </div>
                      <textarea
                        className="field-dark px-5 py-3.5 text-sm w-full mt-4 min-h-[110px] resize-y"
                        placeholder="Note riservate (opzionale): scrivi tutto qui, nessun giudizio, nessun tabù."
                        data-testid="booking-input-messaggio"
                        value={form.messaggio}
                        onChange={set("messaggio")}
                      />
                      <label className="flex items-start gap-3 mt-5 cursor-pointer select-none">
                        <input type="checkbox" data-testid="booking-consent-checkbox" checked={form.consenso} onChange={set("consenso")} className="mt-0.5 w-4 h-4 accent-[#D4A359]" />
                        <span className="text-xs text-silver leading-relaxed">
                          Acconsento a essere ricontattato al numero e all'email indicati per confermare la prenotazione.
                        </span>
                      </label>
                      <div className="flex gap-3 mt-7">
                        <button
                          onClick={() => setStep(1)}
                          data-testid="funnel-step-3-back"
                          className="flex items-center gap-2 border border-white/15 rounded-full px-6 py-3.5 text-sm text-silver hover:text-ivory hover:border-white/30 transition-all"
                        >
                          <ArrowLeft size={16} /> Indietro
                        </button>
                        <button
                          onClick={submit}
                          disabled={sending}
                          data-testid="booking-submit-button"
                          className="flex-1 flex items-center justify-center gap-2 bg-gold text-ink font-semibold rounded-full py-3.5 disabled:opacity-50 hover:bg-goldlight transition-colors"
                        >
                          {sending ? <span className="flex items-center gap-2"><span className="w-4 h-4 rounded-full border-2 border-ink/30 border-t-ink animate-spin" /> Invio…</span> : <span className="flex items-center gap-2"><Send size={16} /> Invia la richiesta</span>}
                        </button>
                      </div>
                      <p className="flex items-center justify-center gap-2 text-[11px] text-silver/70 mt-5">
                        <Lock size={12} /> I tuoi dati sono usati solo per ricontattarti.
                      </p>
                    </div>
                  )}
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </Reveal>
      </div>
    </section>
  );
}
